"use client";

import { useEffect, useState } from "react";
import { PortableText } from "@portabletext/react";

import { GroqImage } from "@/helpers/Image";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import { ChevronLeft, ChevronRight } from "lucide-react";

import dots from "@/public/affiliate-template/more-dots.png";
import Image from "next/image";

export default function TradersSaySection({
  data,
}: {
  data: PageAffiliateSchema;
}) {
  const items = data?.tradersSayItems ?? [];
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;

    const sync = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };

    sync();
    api.on("select", sync);
    api.on("reInit", sync);

    return () => {
      api.off("select", sync);
      api.off("reInit", sync);
    };
  }, [api]);

  if (
    !data?.tradersSayChip &&
    !data?.tradersSayHeading &&
    !items.length &&
    !data?.tradersSayCTA
  ) {
    return null;
  }

  const progress = count > 0 ? ((current + 1) / count) * 100 : 0;

  return (
    <section className="relative lg:px-0 overflow-clip bg-[#FAFAFA] py-[8rem] px-[1rem] lg:py-[15rem]">
      <Image
        src={dots}
        alt=""
        quality={100}
        className="object-cover absolute inset-0 w-full h-full pointer-events-none z-[1] mix-blend-exclusion"
      />
      {/* Header */}
      <div className="mx-auto w-[min(140rem,92%)] relative z-[5]">
        <div className="flex flex-col lg:items-center lg:text-center gap-[1rem] mb-[4rem] lg:mb-[8rem]">
          {data?.tradersSayChip && (
            <span className="italic leading-none text-black font-blacker text-[2rem] font-[400]">
              {data.tradersSayChip}
            </span>
          )}
          {data?.tradersSayHeading && (
            <div className="richtext lg:richtext-center richtext-start prose-headings:text-black prose-headings:font-geist prose-headings:leading-[115%] prose-headings:tracking-[-0.25rem] lg:prose-headings:tracking-[-0.4rem] prose-headings:font-[500]">
              <PortableText
                value={data.tradersSayHeading}
                components={richTextComponent}
              />
            </div>
          )}
        </div>
      </div>

      {/* Cards — full bleed carousel with peeking neighbors */}
      {items.length > 0 && (
        <div className="flex relative flex-col lg:px-0 gap-[4rem] z-[5] px-[1rem] lg:gap-[5rem]">
          <Carousel
            setApi={setApi}
            opts={{ loop: true, align: "center", skipSnaps: false }}
            className="w-full [&_>_div]:!overflow-visible"
          >
            <CarouselContent className="-ml-[2rem] lg:-ml-[2.5rem]">
              {items.map((item, i) => (
                <CarouselItem
                  key={i}
                  className="pl-[2rem] basis-full lg:pl-[2.5rem] lg:basis-[140rem]"
                >
                  <article className="flex flex-col items-center h-full bg-white lg:flex-row rounded-[0.5rem] py-[2rem] gap-[6.5rem] px-[2rem] lg:pl-[4rem]">
                    <div className="flex flex-col justify-between h-full py-[1.5rem]">
                      <div className="flex flex-col gap-[3.25rem]">
                        {item?.chip && (
                          <span className="text-black font-blacker text-[2rem] font-[300] leading-[150%] py-[1rem] px-[1.25rem] bg-[#6DA991]/10 w-fit">
                            {item.chip}
                          </span>
                        )}
                        {item?.text && (
                          <div className="prose-h3:text-[3rem] prose-h3:font-[500] prose-h3:leading-[140%] prose-h3:tracking-[-.72px] prose-h3:font-geist prose-h3:text-[#103037]">
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </div>
                      {item?.name && (
                        <p className="mb-0 font-geist text-[2.5rem] font-[600] leading-[111%] tracking-[-.6px] text-[#0E2E36]">
                          {item.name}
                        </p>
                      )}
                    </div>

                    {item?.certificate && (
                      <div className="overflow-hidden rounded-[0.375rem]  w-[min(76.625rem,100%)] shrink-0">
                        {item.certificate && (
                          <GroqImage
                            quality={100}
                            src={item.certificate as any}
                            alt={item?.name ?? "Certificate"}
                            width={800}
                            height={500}
                            className="object-cover w-full h-auto"
                          />
                        )}
                      </div>
                    )}
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Controls: prev — progress — next */}
          <div className="mx-auto flex w-[min(140rem,92%)] items-center justify-center gap-[2.5rem]">
            <button
              type="button"
              onClick={() => api?.scrollPrev()}
              aria-label="Previous slide"
              className="flex justify-center items-center border-none w-[5rem] h-[5rem] bg-[#C2DED6] rounded-[1rem]"
            >
              <ChevronLeft direction="prev" />
            </button>

            <div
              className="overflow-hidden relative rounded-full h-[2px] w-[20rem] bg-[#001B14]/10 lg:w-[30rem]"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={count}
              aria-valuenow={current + 1}
            >
              <div
                className="absolute top-0 left-0 h-full duration-300 ease-out bg-[#0F7158] transition-[width]"
                style={{ width: `${progress}%` }}
              />
            </div>

            <button
              type="button"
              onClick={() => api?.scrollNext()}
              aria-label="Next slide"
              className="flex justify-center items-center border-none w-[5rem] h-[5rem] bg-[#003828] rounded-[1rem]"
            >
              <ChevronRight direction="next" color="white" />
            </button>
          </div>
        </div>
      )}

      {/* CTA */}
      {data?.tradersSayCTA && (
        <div className="mx-auto w-[min(140rem,92%)] lg:block hidden">
          <div className="flex justify-center mt-[5rem] lg:mt-[7rem]">
            <CTAButton data={data.tradersSayCTA} />
          </div>
        </div>
      )}
    </section>
  );
}
